/**
 * Closes a subtree for editing without closing it for reading.
 *
 * Every write-capable primitive reads this while it is locked. Text, select,
 * textarea and `Combobox` fields show their value as plain text, `Button` is
 * left off, and checkboxes, toggles and radios refuse with the lock's reason.
 * That makes a new control closed by default; one that only reads, navigates
 * or discloses goes inside {@link EditLockExempt}.
 *
 * @module components/ui/EditLock
 */

import { Component, createContext, createRef, type ReactNode, useContext, useMemo } from "react";

export interface EditLockValue {
  locked: boolean;
  /** Shown on each control that refuses itself while locked. */
  reason?: string;
  /** Set by the provider: a lock may close here, so wrappers stay put as it does. */
  governed?: boolean;
}

const UNLOCKED: EditLockValue = { locked: false };

const EditLockContext = createContext<EditLockValue>(UNLOCKED);

interface Held {
  node: HTMLElement;
  dialog: HTMLDialogElement | null;
}

const FOCUSABLE = "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

/**
 * Keeps focus where the lock takes its holder away, in either direction.
 *
 * A focused field that becomes plain text (or back), a button left off, a
 * search box dropped: each loses focus to `body`, so the next Tab starts at
 * the top of the page. A class, because `getSnapshotBeforeUpdate` is the one
 * hook that sees the focused element before React removes it. Two hidden
 * markers bracket the children, so only focus inside them is its to move.
 */
class FocusKeeper extends Component<{ locked: boolean; children: ReactNode }> {
  private start = createRef<HTMLSpanElement>();

  private end = createRef<HTMLSpanElement>();

  getSnapshotBeforeUpdate(previous: { locked: boolean }): Held | null {
    const { locked } = this.props;
    const active = document.activeElement;
    if (previous.locked === locked || !(active instanceof HTMLElement)) {
      return null;
    }
    if (!this.holds(active)) {
      return null;
    }
    return { node: active, dialog: active.closest("dialog") };
  }

  componentDidUpdate(_previous: unknown, _state: unknown, held: Held | null): void {
    if (!held || held.node.isConnected) {
      return;
    }
    // Only a focus the removal dropped: anything else has a holder already.
    const now = document.activeElement;
    if (now && now !== document.body) {
      return;
    }
    // Its stand-in shares the id; otherwise the section is the nearest place.
    const { node, dialog } = held;
    const stand = node.id ? document.getElementById(node.id) : null;
    // Behind showModal() the section is inert, so an open dialog is the nearest place.
    const within = dialog?.isConnected && dialog.open ? dialog : null;
    let target: HTMLElement | null;
    if (stand && this.holds(stand)) {
      target = stand;
    } else if (within) {
      target = within.querySelector<HTMLElement>(FOCUSABLE) ?? within;
    } else {
      // The region the provider sits in, where one takes focus; else the section.
      const region = this.start.current?.parentElement?.closest<HTMLElement>("[tabindex='-1']");
      target = region ?? document.getElementById("content");
    }
    target?.focus({ preventScroll: true });
  }

  private holds(node: Node): boolean {
    const { current: start } = this.start;
    const { current: end } = this.end;
    if (!start || !end) {
      return false;
    }
    const between = document.createRange();
    between.setStartAfter(start);
    between.setEndBefore(end);
    return between.comparePoint(node, 0) === 0;
  }

  render(): ReactNode {
    const { children } = this.props;
    return (
      <>
        <span ref={this.start} hidden />
        {children}
        <span ref={this.end} hidden />
      </>
    );
  }
}

/** Why content cannot be edited: a short form for chrome and a full sentence for controls. */
export interface EditRefusal {
  short?: string;
  full: string;
}

/**
 * Closes its subtree when either input refuses. `decision` is whether the
 * content is editable at all; `lease` is the edit session's lease state for
 * the target, such as another editor holding it. The lease's reason wins: it
 * is the one the reader can wait out.
 */
export function EditLockProvider({
  decision,
  lease = null,
  children,
}: {
  decision: EditRefusal | null;
  lease?: EditRefusal | null;
  children: ReactNode;
}) {
  const refusal = lease ?? decision;
  const locked = refusal !== null;
  const reason = refusal?.full;
  const provided = useMemo(() => ({ locked, reason, governed: true }), [locked, reason]);
  return (
    <EditLockContext.Provider value={provided}>
      <FocusKeeper locked={locked}>{children}</FocusKeeper>
    </EditLockContext.Provider>
  );
}

/** For controls that never write: disclosure, navigation, retry, export. */
export function EditLockExempt({ children }: { children: ReactNode }) {
  return <EditLockContext.Provider value={UNLOCKED}>{children}</EditLockContext.Provider>;
}

export function useEditLock(): EditLockValue {
  return useContext(EditLockContext);
}

/**
 * A primitive's refusal with the lock folded in. The caller's own reason
 * wins when it refuses for itself; otherwise a lock refuses with its own.
 * `offersReason` keeps the reason wrapper mounted, as each primitive's own
 * `"disabledReason" in props` check does.
 */
export function useLockedRefusal(
  disabled: boolean,
  reasonProps: { disabledReason?: string },
): {
  disabled: boolean;
  offersReason: boolean;
  /** A wrapper that may be needed later, so one that never remounts the control. */
  governed: boolean;
  disabledReason: string | undefined;
} {
  const lock = useEditLock();
  const governed = lock.governed === true;
  if (!lock.locked) {
    return {
      disabled,
      governed,
      offersReason: "disabledReason" in reasonProps,
      disabledReason: reasonProps.disabledReason,
    };
  }
  return {
    disabled: true,
    governed,
    offersReason: true,
    disabledReason: disabled ? (reasonProps.disabledReason ?? lock.reason) : lock.reason,
  };
}

/** Per-option refusal for radio groups, folded in the same way. */
export function useLockedOptions<Option extends { disabled?: boolean; disabledReason?: string }>(
  options: readonly Option[],
): readonly Option[] {
  const lock = useEditLock();
  if (!lock.locked) {
    return options;
  }
  return options.map((option) => ({
    ...option,
    disabled: true,
    disabledReason: option.disabled ? (option.disabledReason ?? lock.reason) : lock.reason,
  }));
}
